import {useState, useEffect, useRef, useCallback, useReducer} from '@wordpress/element';
import {useElements, useStripe} from "@stripe/react-stripe-js";
import {ensureErrorResponse, ensureSuccessResponse, getBillingDetailsFromAddress} from "../util";
import {
    reducer,
    useEvents,
    emitFilter,
    constants
} from './actions';

export const useCreatePaymentMethod = (
    {
        name,
        emitResponse,
        billingAddress,
        shippingAddress,
        eventRegistration,
        shouldSavePayment,
        activePaymentMethod,
        shouldCreatePaymentMethod = true
    }) => {
    const [paymentMethodType, setPaymentMethodType] = useState('');
    const {onPaymentSetup} = eventRegistration;
    const stripe = useStripe();
    const elements = useElements();

    const [eventCallbacks, dispatch] = useReducer(reducer, {});
    const events = useEvents(dispatch);

    const currentData = useRef({
        billingAddress,
        shippingAddress,
        paymentMethodData: {},
        eventCallbacks,
        activePaymentMethod
    });

    useEffect(() => {
        currentData.current = {
            ...currentData.current,
            billingAddress,
            eventCallbacks,
            paymentMethodType,
            activePaymentMethod
        };
    }, [
        eventCallbacks,
        billingAddress,
        shippingAddress,
        paymentMethodType,
        activePaymentMethod
    ]);

    const addPaymentMethodData = useCallback((key, value) => {
        currentData.current.paymentMethodData = {...currentData.current.paymentMethodData, [key]: value};
    }, []);

    const createPaymentMethod = useCallback(async () => {
        const {billingAddress, eventCallbacks} = currentData.current;
        try {
            let result = await elements.submit();
            if (result.error) {
                throw result.error;
            }

            if (shouldCreatePaymentMethod) {
                const data = await emitFilter({
                    elements,
                    params: {
                        billing_details: {
                            phone: '',
                            email: '',
                            ...getBillingDetailsFromAddress(billingAddress)
                        }
                    }
                }, null, {events: eventCallbacks, type: constants.CREATE_PAYMENT_DATA});

                result = await stripe.createPaymentMethod(data);

                if (result.error) {
                    throw result.error;
                }
                return result.paymentMethod;
            }
            return {};
        } catch (error) {
            throw error;
        }
    }, [
        stripe,
        elements,
        shouldCreatePaymentMethod
    ]);

    useEffect(() => {
        const unsubscribe = onPaymentSetup((async () => {
            if (activePaymentMethod !== name) {
                return null;
            }
            const {
                eventCallbacks,
                billingAddress,
                shippingAddress,
                paymentMethodType
            } = currentData.current;
            try {
                const paymentMethod = await createPaymentMethod();
                const data = await emitFilter(
                    {
                        meta: {
                            paymentMethodData: {
                                [`${name}_token_key`]: paymentMethod.id,
                                [`${name}_save_source_key`]: shouldSavePayment,
                                _stripe_payment_method_type: paymentMethodType,
                                ...currentData.current.paymentMethodData
                            }
                        }
                    },
                    {paymentMethod, billingAddress, shippingAddress},
                    {events: eventCallbacks, type: constants.ADD_SUCCESS_DATA}
                );

                return ensureSuccessResponse(emitResponse.responseTypes, data);
            } catch (error) {
                return ensureErrorResponse(
                    emitResponse.responseTypes,
                    error,
                    {
                        messageContext: emitResponse.noticeContexts.PAYMENTS
                    }
                );
            }
        }));
        return unsubscribe;
    }, [
        name,
        stripe,
        elements,
        onPaymentSetup,
        shouldSavePayment,
        createPaymentMethod,
        activePaymentMethod
    ]);

    return {
        events,
        paymentMethodType,
        createPaymentMethod,
        setPaymentMethodType,
        addPaymentMethodData
    }
}